import { useApolloClient } from '@apollo/client';
import { SortOrder } from '__generated__/graphql';
import { BASIC_TIME_ENTRIES_QUERY } from 'apollo/queries/time-entry-queries';
import { first } from 'lodash';
import { DateTime } from 'luxon';
import ITimeEntry from 'types/TimeEntry';
import { timeEntryEquipmentOrAddOnFilter } from 'utils/graphql/graphQLFilterUtils';

export interface IFirstTimeEntryArgs {
  memberIds?: string[];
  projectIds?: string[];
  costCodeIds?: string[];
  equipmentIds?: string[];
  includeAddOns?: boolean;
}

function useFirstTimeEntry() {
  const client = useApolloClient();

  async function getFirstTimeEntry(args: IFirstTimeEntryArgs): Promise<ITimeEntry | undefined> {
    const { memberIds, projectIds, costCodeIds, equipmentIds, includeAddOns } = args;

    const isAddOnIncluded = !!includeAddOns;
    const currentTime = DateTime.local().setZone('utc', { keepLocalTime: true });

    const results = await client.query<{ timeEntries: ITimeEntry[] }>({
      query: BASIC_TIME_ENTRIES_QUERY,
      variables: {
        first: 1,
        filter: {
          memberId: memberIds ? { contains: memberIds } : undefined,
          costCodeId: costCodeIds ? { contains: costCodeIds } : undefined,
          equipmentOrAddOn: isAddOnIncluded
            ? timeEntryEquipmentOrAddOnFilter(
                {
                  startTime: DateTime.fromISO('2010-01-01T00:00').startOf('day'),
                  endTime: currentTime.endOf('day').set({ millisecond: 0 }),
                },
                false,
                equipmentIds
              )
            : undefined,
          equipmentId: isAddOnIncluded ? undefined : equipmentIds ? { contains: equipmentIds } : undefined,
          deletedOn: { isNull: true },
          project: projectIds ? { idWithDescendants: { contains: projectIds } } : undefined,
        },
        sort: [{ startTime: SortOrder.Asc }],
      },
      fetchPolicy: 'network-only',
    });

    return first(results.data.timeEntries);
  }

  return getFirstTimeEntry;
}

export default useFirstTimeEntry;
